import { useState } from 'react';
import {
  Box,
  Text,
  Modal,
  SimpleGrid,
  UnstyledButton,
  Group,
  ThemeIcon,
  useMantineTheme,
  useComputedColorScheme,
  ScrollArea,
  TextInput,
  Badge,
} from '@mantine/core';
import { IconPlus, IconSearch } from '@tabler/icons-react';
import { getAllWidgets } from '../registry';
import { useDashboardStore } from '../../../../stores/dashboardStore';
import { WidgetDefinition } from '../types';
import { usePreferencesStore } from '../../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../../hooks/useAccessibilityStyles';

// ============================================================================
// EmptySlot Component
// ============================================================================

export interface EmptySlotProps {
  /** Container instance ID */
  containerInstanceId: string;
  /** Slot ID to add widget to */
  slotId: string;
  /** Whether to show compact version (in global edit mode but not editing container) */
  isCompact?: boolean;
}

export function EmptySlot({ containerInstanceId, slotId, isCompact = false }: EmptySlotProps) {
  const theme = useMantineTheme();
  const colorScheme = useComputedColorScheme('light');
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isDragOver, setIsDragOver] = useState(false);
  const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
  const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

  const accessibilityStyles = {
    lineHeight: LINE_HEIGHT_MAP[lineSpacing],
    letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
  };

  // Store actions
  const addWidgetToContainer = useDashboardStore((state) => state.addWidgetToContainer);
  const moveWidgetToContainer = useDashboardStore((state) => state.moveWidgetToContainer);
  const draggedWidgetId = useDashboardStore((state) => state.draggedWidgetId);
  const isEditMode = useDashboardStore((state) => state.isEditMode);

  // Get all widgets and filter by search
  const allWidgets = getAllWidgets();
  const filteredWidgets = searchQuery
    ? allWidgets.filter(
        (w) =>
          w.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          w.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
          w.category.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : allWidgets;

  // Prefer stat widgets for containers
  const statWidgets = filteredWidgets.filter(
    (w) => w.category === 'stats' || w.displayStyle === 'stat'
  );
  const otherWidgets = filteredWidgets.filter(
    (w) => w.category !== 'stats' && w.displayStyle !== 'stat'
  );

  // Handle widget selection
  const handleSelectWidget = (widget: WidgetDefinition) => {
    addWidgetToContainer(containerInstanceId, slotId, widget.id);
    setIsPickerOpen(false);
    setSearchQuery('');
  };

  // Handle drag over
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    setIsDragOver(true);
  };

  // Handle drag leave
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  // Handle drop from dashboard widget
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const widgetInstanceId = e.dataTransfer.getData('text/plain');
    if (widgetInstanceId) {
      moveWidgetToContainer(widgetInstanceId, containerInstanceId, slotId);
    }
  };

  // Widget card component
  const WidgetCard = ({ widget }: { widget: WidgetDefinition }) => {
    const Icon = widget.icon;
    return (
      <UnstyledButton
        onClick={() => handleSelectWidget(widget)}
        style={{
          padding: theme.spacing.sm,
          borderRadius: theme.radius.md,
          backgroundColor:
            colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[0],
          border: `1px solid ${
            colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[3]
          }`,
          transition: 'all 150ms ease',
          '&:hover': {
            backgroundColor:
              colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.gray[1],
          },
        }}
      >
        <Group gap="sm" wrap="nowrap">
          <ThemeIcon size="md" variant="light" color="blue">
            <Icon size={16} />
          </ThemeIcon>
          <Box style={{ flex: 1, minWidth: 0 }}>
            <Text size="sm" fw={500} lineClamp={1} style={accessibilityStyles}>
              {widget.name}
            </Text>
            <Text size="xs" c="dimmed" lineClamp={1} style={accessibilityStyles}>
              {widget.description}
            </Text>
          </Box>
          <Badge size="xs" variant="light" color="gray">
            {widget.category}
          </Badge>
        </Group>
      </UnstyledButton>
    );
  };

  return (
    <>
      {/* Empty slot placeholder */}
      <UnstyledButton
        onClick={() => setIsPickerOpen(true)}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        style={{
          height: '100%',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: isCompact ? 2 : theme.spacing.xs,
          borderRadius: theme.radius.md,
          border: `${isCompact ? '1px' : '2px'} dashed ${
            isDragOver
              ? theme.colors.blue[5]
              : colorScheme === 'dark'
                ? theme.colors.dark[4]
                : theme.colors.gray[4]
          }`,
          backgroundColor: isDragOver
            ? colorScheme === 'dark'
              ? 'rgba(59, 130, 246, 0.15)'
              : 'rgba(59, 130, 246, 0.1)'
            : colorScheme === 'dark'
              ? 'rgba(255, 255, 255, 0.02)'
              : 'rgba(0, 0, 0, 0.02)',
          cursor: 'pointer',
          transition: 'all 150ms ease',
          minHeight: isCompact ? 40 : undefined,
          transform: isDragOver ? 'scale(1.02)' : undefined,
        }}
      >
        <ThemeIcon
          size={isCompact ? 'sm' : 'lg'}
          variant="light"
          color={isDragOver ? 'blue' : 'gray'}
          radius="xl"
        >
          <IconPlus size={isCompact ? 12 : 18} />
        </ThemeIcon>
        {!isCompact && (
          <Text size="xs" c={isDragOver ? 'blue' : 'dimmed'} style={accessibilityStyles}>
            {isDragOver ? 'Drop here' : 'Add widget'}
          </Text>
        )}
      </UnstyledButton>

      {/* Widget picker modal */}
      <Modal
        opened={isPickerOpen}
        onClose={() => {
          setIsPickerOpen(false);
          setSearchQuery('');
        }}
        title="Add Widget to Container"
        size="lg"
        centered
      >
        <TextInput
          placeholder="Search widgets..."
          leftSection={<IconSearch size={16} />}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.currentTarget.value)}
          mb="md"
        />

        <ScrollArea h={400}>
          {/* Stat widgets section */}
          {statWidgets.length > 0 && (
            <Box mb="md">
              <Text size="sm" fw={600} mb="xs" c="dimmed" style={accessibilityStyles}>
                Recommended for containers
              </Text>
              <SimpleGrid cols={1} spacing="xs">
                {statWidgets.map((widget) => (
                  <WidgetCard key={widget.id} widget={widget} />
                ))}
              </SimpleGrid>
            </Box>
          )}

          {/* Other widgets section */}
          {otherWidgets.length > 0 && (
            <Box>
              <Text size="sm" fw={600} mb="xs" c="dimmed" style={accessibilityStyles}>
                Other widgets
              </Text>
              <SimpleGrid cols={1} spacing="xs">
                {otherWidgets.map((widget) => (
                  <WidgetCard key={widget.id} widget={widget} />
                ))}
              </SimpleGrid>
            </Box>
          )}

          {/* No results */}
          {filteredWidgets.length === 0 && (
            <Box
              style={{
                textAlign: 'center',
                padding: theme.spacing.xl,
                color: theme.colors.gray[5],
              }}
            >
              <Text size="sm" style={accessibilityStyles}>No widgets found matching "{searchQuery}"</Text>
            </Box>
          )}
        </ScrollArea>
      </Modal>
    </>
  );
}

export default EmptySlot;
